import { useState, useEffect } from 'react';

interface Property {
  id: string;
  title: string;
  description: string;
  property_type: string;
  operation_type: string;
  price_sale?: number;
  price_rent?: number;
  currency: string;
  location: string;
  city: string;
  bedrooms?: number;
  bathrooms?: string;
  area: number;
  area_unit: string;
  parking_spaces?: number;
  floor?: number;
  total_floors?: number;
  year_built?: number;
  features: string[];
  images: string[];
  is_featured: boolean;
  is_active: boolean;
  available_for_sale: boolean;
  available_for_rent: boolean;
  maintenance_fee?: number;
  maintenance_fee_status?: string;
  has_maintenance_fee?: boolean;
  included_services: string[];
  created_at: string;
  updated_at: string;
}

interface Service {
  value: string;
  label: string;
  icon: string;
}

const availableServices: Service[] = [
  { value: 'water', label: 'Agua', icon: 'ri-drop-line' },
  { value: 'electricity', label: 'Luz', icon: 'ri-flashlight-line' },
  { value: 'gas', label: 'Gas', icon: 'ri-fire-line' },
  { value: 'internet', label: 'Internet', icon: 'ri-wifi-line' },
  { value: 'cable', label: 'Cable TV', icon: 'ri-tv-line' },
  { value: 'security', label: 'Seguridad', icon: 'ri-shield-check-line' },
  { value: 'cleaning', label: 'Limpieza', icon: 'ri-brush-line' },
  { value: 'maintenance', label: 'Mantenimiento', icon: 'ri-tools-line' }
];

export default function PropertiesManagement() {
  const [properties, setProperties] = useState<Property[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    property_type: 'departamento',
    operation_type: 'venta',
    price_sale: '',
    price_rent: '',
    currency: 'MXN',
    location: '',
    city: '',
    bedrooms: '',
    bathrooms: '',
    area: '',
    area_unit: 'm2',
    parking_spaces: '',
    floor: '',
    total_floors: '',
    year_built: '',
    features: [] as string[],
    images: [] as string[],
    is_featured: false,
    is_active: true,
    available_for_sale: true,
    available_for_rent: false,
    maintenance_fee: '',
    maintenance_fee_status: 'not_applicable',
    has_maintenance_fee: false,
    included_services: [] as string[]
  });

  useEffect(() => {
    fetchProperties();
    fetchCities();
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/properties-api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ action: 'all' })
      });

      if (response.ok) {
        const data = await response.json();
        setProperties(data.properties || []);
      }
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/properties-api`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ action: 'cities' })
      });

      if (response.ok) {
        const data = await response.json();
        setCities(data.cities || []);
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const formatNumberWithCommas = (value: string) => {
    const number = value.replace(/,/g, '');
    if (!number || isNaN(Number(number))) return '';
    return Number(number).toLocaleString('en-US');
  };

  const handlePriceChange = (field: string, value: string) => {
    const numericValue = value.replace(/,/g, '');
    if (numericValue === '' || /^\d+$/.test(numericValue)) {
      setFormData({ ...formData, [field]: numericValue });
    }
  };

  const toggleIncludedService = (service: string) => {
    const currentServices = formData.included_services || [];
    if (currentServices.includes(service)) {
      setFormData({
        ...formData,
        included_services: currentServices.filter(s => s !== service)
      });
    } else {
      setFormData({
        ...formData,
        included_services: [...currentServices, service]
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!formData.title.trim()) {
      alert('El título es obligatorio');
      return;
    }

    if (!formData.description.trim()) {
      alert('La descripción es obligatoria');
      return;
    }

    if (!formData.location.trim()) {
      alert('La ubicación es obligatoria');
      return;
    }

    if (!formData.city.trim()) {
      alert('La ciudad es obligatoria');
      return;
    }

    if (!formData.area || parseFloat(formData.area) <= 0) {
      alert('El área debe ser mayor a 0');
      return;
    }

    // Validar precios según tipo de operación
    if (formData.operation_type === 'venta' || formData.operation_type === 'ambos') {
      if (!formData.price_sale || parseFloat(formData.price_sale) <= 0) {
        alert('El precio de venta debe ser mayor a 0');
        return;
      }
      if (parseFloat(formData.price_sale) > 999999999) {
        alert('El precio de venta no puede exceder $999,999,999');
        return;
      }
    }

    if (formData.operation_type === 'renta' || formData.operation_type === 'ambos') {
      if (!formData.price_rent || parseFloat(formData.price_rent) <= 0) {
        alert('El precio de renta debe ser mayor a 0');
        return;
      }
      if (parseFloat(formData.price_rent) > 999999999) {
        alert('El precio de renta no puede exceder $999,999,999');
        return;
      }

      // Validar cuota de mantenimiento si está incluida
      if (formData.maintenance_fee_status === 'included') {
        if (!formData.maintenance_fee || parseFloat(formData.maintenance_fee) <= 0) {
          alert('El importe de la cuota de mantenimiento debe ser mayor a 0');
          return;
        }
        if (parseFloat(formData.maintenance_fee) > 999999999) {
          alert('La cuota de mantenimiento no puede exceder $999,999,999');
          return;
        }
      }
    }

    try {
      const propertyData: any = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        property_type: formData.property_type,
        currency: formData.currency,
        location: formData.location.trim(),
        city: formData.city.trim(),
        area: parseFloat(formData.area),
        area_unit: formData.area_unit,
        features: formData.features,
        images: formData.images,
        is_featured: formData.is_featured,
        is_active: formData.is_active,
        available_for_sale: formData.operation_type === 'venta' || formData.operation_type === 'ambos',
        available_for_rent: formData.operation_type === 'renta' || formData.operation_type === 'ambos'
      };

      // Agregar precio de venta si aplica
      if (formData.operation_type === 'venta' || formData.operation_type === 'ambos') {
        propertyData.price_sale = parseFloat(formData.price_sale);
      }

      // Agregar precio de renta si aplica
      if (formData.operation_type === 'renta' || formData.operation_type === 'ambos') {
        propertyData.price_rent = parseFloat(formData.price_rent);
        propertyData.included_services = formData.included_services || [];
        propertyData.maintenance_fee_status = formData.maintenance_fee_status;
        
        if (formData.maintenance_fee_status === 'included') {
          propertyData.maintenance_fee = parseFloat(formData.maintenance_fee);
          propertyData.has_maintenance_fee = true;
        } else {
          propertyData.maintenance_fee = null;
          propertyData.has_maintenance_fee = false;
        }
      }

      // Campos opcionales numéricos
      if (formData.bedrooms) propertyData.bedrooms = parseInt(formData.bedrooms);
      if (formData.parking_spaces) propertyData.parking_spaces = parseInt(formData.parking_spaces);
      if (formData.floor) propertyData.floor = parseInt(formData.floor);
      if (formData.total_floors) propertyData.total_floors = parseInt(formData.total_floors);
      if (formData.year_built) propertyData.year_built = parseInt(formData.year_built);

      // Campo de baños (puede ser texto o número)
      if (formData.bathrooms) {
        propertyData.bathrooms = formData.bathrooms;
      }

      const url = editingId
        ? `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/properties-admin-enhanced`
        : `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/properties-admin-enhanced`;

      const response = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify(editingId ? { id: editingId, ...propertyData } : propertyData)
      });

      if (response.ok) {
        alert(editingId ? 'Propiedad actualizada exitosamente' : 'Propiedad creada exitosamente');
        setShowForm(false);
        setEditingId(null);
        resetForm();
        fetchProperties();
      } else {
        const error = await response.text();
        throw new Error(error);
      }
    } catch (error) {
      console.error('Error saving property:', error);
      alert('Error al guardar la propiedad');
    }
  };

  const handleEdit = (property: Property) => {
    // Determinar el tipo de operación
    let operationType = 'venta';
    if (property.available_for_sale && property.available_for_rent) {
      operationType = 'ambos';
    } else if (property.available_for_rent) {
      operationType = 'renta';
    }

    // Determinar el estado de la cuota de mantenimiento
    let maintenanceFeeStatus = 'not_applicable';
    if (property.available_for_rent) {
      if (property.maintenance_fee && property.maintenance_fee > 0) {
        maintenanceFeeStatus = 'included';
      } else if (property.has_maintenance_fee === false) {
        maintenanceFeeStatus = 'not_applicable';
      } else {
        maintenanceFeeStatus = 'not_included';
      }
    }

    setFormData({
      title: property.title,
      description: property.description,
      property_type: property.property_type,
      operation_type: operationType,
      price_sale: property.price_sale?.toString() || '',
      price_rent: property.price_rent?.toString() || '',
      currency: property.currency,
      location: property.location,
      city: property.city,
      bedrooms: property.bedrooms?.toString() || '',
      bathrooms: property.bathrooms?.toString() || '',
      area: property.area.toString(),
      area_unit: property.area_unit,
      parking_spaces: property.parking_spaces?.toString() || '',
      floor: property.floor?.toString() || '',
      total_floors: property.total_floors?.toString() || '',
      year_built: property.year_built?.toString() || '',
      features: property.features || [],
      images: property.images || [],
      is_featured: property.is_featured,
      is_active: property.is_active,
      available_for_sale: property.available_for_sale,
      available_for_rent: property.available_for_rent,
      maintenance_fee: property.maintenance_fee?.toString() || '',
      maintenance_fee_status: maintenanceFeeStatus,
      has_maintenance_fee: property.has_maintenance_fee || false,
      included_services: property.included_services || []
    });
    setEditingId(property.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar esta propiedad?')) {
      return;
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/properties-admin-enhanced`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({ id })
      });

      if (response.ok) {
        alert('Propiedad eliminada exitosamente');
        fetchProperties();
      } else {
        throw new Error('Error al eliminar');
      }
    } catch (error) {
      console.error('Error deleting property:', error);
      alert('Error al eliminar la propiedad');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      property_type: 'departamento',
      operation_type: 'venta',
      price_sale: '',
      price_rent: '',
      currency: 'MXN',
      location: '',
      city: '',
      bedrooms: '',
      bathrooms: '',
      area: '',
      area_unit: 'm2',
      parking_spaces: '',
      floor: '',
      total_floors: '',
      year_built: '',
      features: [],
      images: [],
      is_featured: false,
      is_active: true,
      available_for_sale: true,
      available_for_rent: false,
      maintenance_fee: '',
      maintenance_fee_status: 'not_applicable',
      has_maintenance_fee: false,
      included_services: []
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 animate-spin">
          <i className="ri-loader-4-line text-2xl text-blue-600"></i>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestión de Propiedades</h2>
        <button
          onClick={() => {
            setShowForm(true);
            setEditingId(null);
            resetForm();
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-add-line mr-2"></i>
          Nueva Propiedad
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">
            {editingId ? 'Editar Propiedad' : 'Nueva Propiedad'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Título */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Título *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Descripción */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              {/* Tipo de Propiedad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Propiedad *
                </label>
                <select
                  value={formData.property_type}
                  onChange={(e) => setFormData({ ...formData, property_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="departamento">Departamento</option>
                  <option value="casa">Casa</option>
                  <option value="terreno">Terreno</option>
                  <option value="oficina">Oficina</option>
                  <option value="local_comercial">Local Comercial</option>
                </select>
              </div>

              {/* Tipo de Operación */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Operación *
                </label>
                <select
                  value={formData.operation_type}
                  onChange={(e) => {
                    const newOperationType = e.target.value;
                    setFormData({
                      ...formData,
                      operation_type: newOperationType,
                      available_for_sale: newOperationType === 'venta' || newOperationType === 'ambos',
                      available_for_rent: newOperationType === 'renta' || newOperationType === 'ambos',
                      maintenance_fee_status: newOperationType === 'renta' || newOperationType === 'ambos' ? 'not_applicable' : 'not_applicable'
                    });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="venta">Solo Venta</option>
                  <option value="renta">Solo Renta</option>
                  <option value="ambos">Venta y Renta</option>
                </select>
              </div>

              {/* Precio de Venta */}
              {(formData.operation_type === 'venta' || formData.operation_type === 'ambos') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio de Venta *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                      {formData.currency === 'USD' ? 'US$' : '$'}
                    </span>
                    <input
                      type="text"
                      value={formData.price_sale ? formatNumberWithCommas(formData.price_sale) : ''}
                      onChange={(e) => handlePriceChange('price_sale', e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="1,250,000"
                      required={formData.operation_type === 'venta' || formData.operation_type === 'ambos'}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Ingrese solo números (ej: 1250000)</p>
                </div>
              )}

              {/* Precio de Renta */}
              {(formData.operation_type === 'renta' || formData.operation_type === 'ambos') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Precio de Renta Mensual *
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                      {formData.currency === 'USD' ? 'US$' : '$'}
                    </span>
                    <input
                      type="text"
                      value={formData.price_rent ? formatNumberWithCommas(formData.price_rent) : ''}
                      onChange={(e) => handlePriceChange('price_rent', e.target.value)}
                      className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      placeholder="15,000"
                      required={formData.operation_type === 'renta' || formData.operation_type === 'ambos'}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Ingrese solo números (ej: 15000)</p>
                </div>
              )}

              {/* Moneda */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Moneda *
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="MXN">MXN (Pesos Mexicanos)</option>
                  <option value="USD">USD (Dólares)</option>
                </select>
              </div>

              {/* Ubicación */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ubicación *
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Colonia, Calle, Número"
                  required
                />
              </div>

              {/* Ciudad */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ciudad *
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  list="cities-list"
                  required
                />
                <datalist id="cities-list">
                  {cities.map((city) => (
                    <option key={city} value={city} />
                  ))}
                </datalist>
              </div>

              {/* Área */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Área *
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={formData.area}
                    onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.01"
                    required
                  />
                  <select
                    value={formData.area_unit}
                    onChange={(e) => setFormData({ ...formData, area_unit: e.target.value })}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="m2">m²</option>
                    <option value="ft2">ft²</option>
                  </select>
                </div>
              </div>

              {/* Recámaras */}
              {(formData.property_type === 'departamento' || formData.property_type === 'casa') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recámaras
                  </label>
                  <input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                  />
                </div>
              )}

              {/* Baños */}
              {(formData.property_type === 'departamento' || formData.property_type === 'casa') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Baños
                  </label>
                  <input
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    min="0"
                    step="0.5"
                  />
                </div>
              )}

              {/* Baños para Oficinas y Locales Comerciales */}
              {(formData.property_type === 'oficina' || formData.property_type === 'local_comercial') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Baños
                  </label>
                  <input
                    type="text"
                    value={formData.bathrooms}
                    onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Ej: 2, Incluidos en el piso, Compartidos"
                  />
                  <p className="text-xs text-gray-500 mt-1">Puede ingresar un número o texto descriptivo</p>
                </div>
              )}

              {/* Estacionamientos */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estacionamientos
                </label>
                <input
                  type="number"
                  value={formData.parking_spaces}
                  onChange={(e) => setFormData({ ...formData, parking_spaces: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="0"
                />
              </div>

              {/* Piso */}
              {formData.property_type === 'departamento' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Piso
                    </label>
                    <input
                      type="number"
                      value={formData.floor}
                      onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Total de Pisos
                    </label>
                    <input
                      type="number"
                      value={formData.total_floors}
                      onChange={(e) => setFormData({ ...formData, total_floors: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      min="0"
                    />
                  </div>
                </>
              )}

              {/* Año de Construcción */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Año de Construcción
                </label>
                <input
                  type="number"
                  value={formData.year_built}
                  onChange={(e) => setFormData({ ...formData, year_built: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  min="1900"
                  max={new Date().getFullYear()}
                />
              </div>

              {/* Switches */}
              <div className="col-span-2 flex gap-6">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Propiedad Destacada</span>
                </label>

                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Propiedad Activa</span>
                </label>
              </div>

              {/* Información Adicional de Renta */}
              {formData.available_for_rent && (
              <div className="col-span-2 space-y-4">
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <i className="ri-service-line mr-2 text-blue-600"></i>
                    Información Adicional de Renta
                  </h4>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      <i className="ri-tools-line mr-1 text-blue-600"></i>
                      Estado de Cuota de Mantenimiento *
                    </label>
                    
                    <div className="space-y-3">
                      <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${
                        formData.maintenance_fee_status === 'included' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}>
                        <input
                          type="radio"
                          name="maintenance_fee_status"
                          value="included"
                          checked={formData.maintenance_fee_status === 'included'}
                          onChange={(e) => setFormData({
                            ...formData, 
                            maintenance_fee_status: 'included'
                          })}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div className="ml-3 flex-1">
                          <span className="text-sm font-medium text-gray-900">
                            <i className="ri-check-line mr-1 text-green-600"></i>
                            Incluye Cuota de Mantenimiento
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            La renta incluye la cuota de mantenimiento. Especifique el importe.
                          </p>
                        </div>
                      </label>

                      <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${
                        formData.maintenance_fee_status === 'not_included' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}>
                        <input
                          type="radio"
                          name="maintenance_fee_status"
                          value="not_included"
                          checked={formData.maintenance_fee_status === 'not_included'}
                          onChange={(e) => setFormData({
                            ...formData, 
                            maintenance_fee_status: 'not_included',
                            maintenance_fee: ''
                          })}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div className="ml-3 flex-1">
                          <span className="text-sm font-medium text-gray-900">
                            <i className="ri-close-circle-line mr-1 text-orange-600"></i>
                            Cuota de Mantenimiento No Incluida
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            El inquilino debe pagar la cuota de mantenimiento por separado.
                          </p>
                        </div>
                      </label>

                      <label className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${
                        formData.maintenance_fee_status === 'not_applicable' ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                      }`}>
                        <input
                          type="radio"
                          name="maintenance_fee_status"
                          value="not_applicable"
                          checked={formData.maintenance_fee_status === 'not_applicable'}
                          onChange={(e) => setFormData({
                            ...formData, 
                            maintenance_fee_status: 'not_applicable',
                            maintenance_fee: ''
                          })}
                          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <div className="ml-3 flex-1">
                          <span className="text-sm font-medium text-gray-900">
                            <i className="ri-forbid-line mr-1 text-gray-600"></i>
                            No Aplica Cuota de Mantenimiento
                          </span>
                          <p className="text-xs text-gray-500 mt-1">
                            Este inmueble no cuenta con cuota de mantenimiento.
                          </p>
                        </div>
                      </label>
                    </div>

                    {formData.maintenance_fee_status === 'included' && (
                      <div className="mt-4 ml-6">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Importe de Cuota de Mantenimiento *
                        </label>
                        <div className="relative max-w-xs">
                          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">
                            {formData.currency === 'USD' ? 'US$' : '$'}
                          </span>
                          <input
                            type="text"
                            value={formData.maintenance_fee ? formatNumberWithCommas(formData.maintenance_fee) : ''}
                            onChange={(e) => handlePriceChange('maintenance_fee', e.target.value)}
                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            placeholder="2,500"
                            required={formData.maintenance_fee_status === 'included'}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Ingrese solo números (ej: 2500)</p>
                      </div>
                    )}
                  </div>

                  <div>
                    <h5 className="text-sm font-medium text-gray-700 mb-3">
                      <i className="ri-checkbox-multiple-line mr-1 text-blue-600"></i>
                      Servicios Incluidos en la Renta
                    </h5>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {availableServices.map((service) => (
                        <button
                          key={service.value}
                          type="button"
                          onClick={() => toggleIncludedService(service.value)}
                          className={`px-4 py-3 rounded-lg border-2 transition-all cursor-pointer ${
                            formData.included_services.includes(service.value)
                              ? 'bg-blue-50 border-blue-500 text-blue-700'
                              : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex flex-col items-center gap-2">
                            <i className={`${service.icon} text-2xl`}></i>
                            <span className="text-sm font-medium">{service.label}</span>
                            {formData.included_services.includes(service.value) && (
                              <i className="ri-check-line text-blue-600"></i>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            </div>

            {/* Botones */}
            <div className="flex justify-end gap-3 pt-4 border-t">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  resetForm();
                }}
                className="px-6 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-save-line mr-2"></i>
                {editingId ? 'Actualizar' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Propiedades */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Propiedad
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo / Operación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Precio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ubicación
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {properties.map((property) => (
                <tr key={property.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{property.title}</div>
                    <div className="text-sm text-gray-500">{property.area} {property.area_unit}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{property.property_type}</div>
                    <div className="text-sm text-gray-500">
                      {property.available_for_sale && property.available_for_rent ? 'Venta y Renta' :
                       property.available_for_sale ? 'Venta' : 'Renta'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {property.price_sale && (
                      <div className="text-sm text-gray-900">
                        Venta: {property.currency === 'USD' ? 'US$' : '$'}{property.price_sale.toLocaleString()}
                      </div>
                    )}
                    {property.price_rent && (
                      <div className="text-sm text-gray-900">
                        Renta: {property.currency === 'USD' ? 'US$' : '$'}{property.price_rent.toLocaleString()}/mes
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{property.city}</div>
                    <div className="text-sm text-gray-500">{property.location}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col gap-1">
                      {property.is_active && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                          Activa
                        </span>
                      )}
                      {property.is_featured && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                          Destacada
                        </span>
                      )}
                      {!property.is_active && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                          Inactiva
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(property)}
                        className="text-blue-600 hover:text-blue-900 cursor-pointer"
                        title="Editar"
                      >
                        <i className="ri-edit-line text-lg"></i>
                      </button>
                      <button
                        onClick={() => handleDelete(property.id)}
                        className="text-red-600 hover:text-red-900 cursor-pointer"
                        title="Eliminar"
                      >
                        <i className="ri-delete-bin-line text-lg"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {properties.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-building-line text-4xl text-gray-400 mb-2"></i>
            <p className="text-gray-500">No hay propiedades registradas</p>
          </div>
        )}
      </div>
    </div>
  );
}
